import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, Descriptions, Button, Space, Tag, Typography, Row, Col, Spin, Tabs, message } from 'antd';
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAmenityById, deleteAmenity, updateAmenity, getBlocks } from '@/api/properties';
import { Amenity } from '@/types/resources/property';
import { useAuth } from '@/contexts/AuthContext';
import ActivityLogList from '@/components/common/ActivityLogList';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import AmenityFormModal from '@/components/features/resources/properties/AmenityFormModal';

const { Title } = Typography;

const AmenityDetails: React.FC = () => {
  const { t } = useTranslation('properties');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteError, setDeleteError] = useState<Error & { dependencies?: any[]; amenityName?: string } | undefined>(undefined);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const state = location.state as { amenity: Amenity } | null;

  // Get amenity data
  const { data: amenity, isLoading } = useQuery({
    queryKey: ['amenity', id, firestoreUser?.workspace?.id],
    queryFn: () => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return getAmenityById(id!, firestoreUser);
    },
    enabled: !!id && !!firestoreUser && !state?.amenity,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get blocks for the form
  const { data: blocksData = { blocks: [] } } = useQuery({
    queryKey: ['blocks', firestoreUser?.workspace?.id],
    queryFn: () => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return getBlocks({ firestoreUser });
    },
    enabled: !!firestoreUser,
    staleTime: 5 * 60 * 1000,
  });

  // Use state data if available, otherwise use fetched data
  const displayAmenity = state?.amenity || amenity;

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Amenity> }) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return updateAmenity(id, data, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('amenity.messages.updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['amenities', id] });
      setIsModalVisible(false);
    },
    onError: (error: Error) => {
      message.error(t('amenity.messages.updateError'));
      console.error('Error updating amenity:', error);
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteAmenity(id, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('amenity.messages.deleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['amenities'] });
      navigate('/resources/properties/amenities');
    },
    onError: (error: Error) => {
      if ((error as any).dependencies) {
        setDeleteError(error as any);
      } else {
        message.error(t('amenity.messages.deleteError'));
        setShowDeleteModal(false);
      }
      console.error('Error deleting amenity:', error);
    }
  });

  const handleEdit = () => {
    setIsModalVisible(true);
  };

  const handleModalSubmit = (values: Partial<Amenity>) => {
    if (displayAmenity?.id) {
      updateMutation.mutate({ id: displayAmenity.id, data: values });
    }
  };

  const handleDelete = () => {
    if (id) {
      deleteMutation.mutate(id);
      setShowDeleteModal(false);
    }
  };

  if (isLoading && !state?.amenity) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!displayAmenity) {
    return <div>{t('common.notFound')}</div>;
  }

  const tabItems = [
    {
      key: 'overview',
      label: t('common:details'),
      children: (
        <Card>
          <Descriptions bordered column={1}>
            <Descriptions.Item label={t('fields.name')}>
              {displayAmenity.name}
            </Descriptions.Item>
            <Descriptions.Item label={t('fields.type')}>
              <Tag color="blue">{t(`amenity.types.${displayAmenity.type}`)}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('fields.description')}>
              {displayAmenity.description || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('fields.status')}>
              <Tag color={displayAmenity.status === 'active' ? 'green' : displayAmenity.status === 'maintenance' ? 'orange' : 'red'}>
                {t(`amenity.status.${displayAmenity.status}`)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('fields.capacity')}>
              {displayAmenity.capacity || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('fields.block')}>
              {displayAmenity.block?.name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('fields.guideline')}>
              {displayAmenity.guideline?.name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('fields.operatingHours')}>
              {displayAmenity.operatingHours ? (
                <div>
                  {displayAmenity.operatingHours.open} - {displayAmenity.operatingHours.close}
                  <br />
                  {displayAmenity.operatingHours.days.join(', ')}
                </div>
              ) : '-'}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      ),
    },
    {
      key: 'activityLog',
      label: t('common:activityLog'),
      children: (
        <Card>
          <ActivityLogList activityRecords={Array.isArray(displayAmenity.activityRecords) ? displayAmenity.activityRecords : []} />
        </Card>
      ),
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/resources/properties/amenities')}>
              {t('common:back')}
            </Button>
            <Title level={3} style={{ margin: 0 }}>{displayAmenity.name}</Title>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Button icon={<EditOutlined />} onClick={handleEdit}>
              {t('common:actions.edit')}
            </Button>
            <Button 
              danger 
              icon={<DeleteOutlined />} 
              onClick={() => setShowDeleteModal(true)}
              loading={deleteMutation.isPending}
            >
              {t('common:actions.delete')}
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Content */}
      <Tabs items={tabItems} />

      <AmenityFormModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSubmit={handleModalSubmit}
        initialValues={displayAmenity}
        title={t('amenity.actions.edit')}
        blocks={blocksData.blocks.map(block => ({ id: block.id, name: block.name }))}
      />

      <DeleteConfirmationModal
        visible={showDeleteModal}
        onCancel={() => {
          setShowDeleteModal(false);
          setDeleteError(undefined);
        }}
        onConfirm={handleDelete}
        title={t('properties:amenity.messages.deleteConfirm')}
        error={deleteError}
        onErrorDismiss={() => setDeleteError(undefined)}
      />
    </div>
  );
};

export default AmenityDetails; 